import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SEO, getBreadcrumbSchema } from '@/components/seo/SEO';
import { 
  FloralCorner, 
  FloralSeparator,
  PaperLayerBg 
} from '@/components/ui/FloralElements';
import { 
  Star,
  MessageSquare,
  Facebook,
  ExternalLink,
  Heart,
  Quote
} from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Real Facebook reviews
const facebookReviews = [
  {
    id: 1,
    name: 'Kris House',
    rating: 5,
    text: 'Great products made with love! 10/10',
    source: 'Facebook'
  },
  {
    id: 2,
    name: 'Chelsie Deaver',
    rating: 5,
    text: "My family loves Little House on the Table's jams and jellies. My absolute favorite is the peach jalapeño jam! The perfect balance of sweet with just a little kick. I love serving it over cream cheese with crackers, and it's always a hit. You can really taste the quality and care put into their products. 10/10 recommend and will definitely be ordering again!",
    source: 'Facebook'
  },
  {
    id: 3,
    name: 'Megan Grossmann',
    rating: 5,
    text: 'Candy Jalapeños 😍 Jalapeño a day keeps the DR. away HIGHLY recommend',
    source: 'Facebook'
  }
];

export const ReviewsPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Reviews', path: '/reviews' }
  ];

  return (
    <>
      <SEO
        title="Customer Reviews"
        description="Read what our customers say about Little House on the Table products. Real reviews from real people who love our handmade goat milk soaps, jams, jellies, and more."
        path="/reviews"
        structuredData={getBreadcrumbSchema(breadcrumbs)}
        keywords="customer reviews, testimonials, goat milk soap reviews, jam reviews, Nebraska ranch products, customer feedback"
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-hero overflow-hidden">
        <FloralCorner position="top-left" className="opacity-40" />
        <FloralCorner position="bottom-right" className="opacity-40" />
        
        <div className="container mx-auto px-4 md:px-6 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4"
            >
              <MessageSquare className="h-4 w-4" />
              Customer Love
            </motion.span>
            <motion.h1 
              variants={fadeInUp}
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6"
            >
              Reviews & Testimonials
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-muted-foreground"
            >
              Real reviews from real customers who love our handmade ranch products.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-secondary">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="flex flex-wrap items-center justify-center gap-8"
          >
            <motion.div variants={fadeInUp} className="text-center">
              <div className="flex items-center justify-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-floral-sunflower fill-floral-sunflower" />
                ))}
              </div>
              <p className="text-sm text-muted-foreground">5 Star Reviews</p>
            </motion.div>
            <motion.div variants={fadeInUp} className="text-center">
              <p className="text-3xl font-bold text-foreground mb-1">10/10</p>
              <p className="text-sm text-muted-foreground">Customer Rating</p>
            </motion.div>
            <motion.div variants={fadeInUp} className="text-center">
              <p className="text-3xl font-bold text-foreground mb-1">100%</p>
              <p className="text-sm text-muted-foreground">Would Recommend</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Facebook Reviews */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4"
            >
              What Customers Are Saying
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-muted-foreground flex items-center justify-center gap-2"
            >
              <Facebook className="h-5 w-5" />
              Reviews from our Facebook page
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {facebookReviews.map((review) => (
              <motion.div key={review.id} variants={fadeInUp}>
                <Card className="raised-card h-full border-0 relative overflow-hidden">
                  <FloralCorner position="top-right" className="opacity-15 w-12 h-12" />
                  <CardContent className="p-6 flex flex-col h-full">
                    {/* Quote icon */}
                    <Quote className="h-8 w-8 text-primary/20 mb-3" />
                    
                    {/* Stars */}
                    <div className="flex gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <Star key={i} className="h-4 w-4 text-floral-sunflower fill-floral-sunflower" />
                      ))}
                    </div>
                    
                    {/* Review text */}
                    <p className="text-foreground mb-4 flex-grow">
                      "{review.text}"
                    </p>
                    
                    {/* Author */}
                    <div className="flex items-center justify-between pt-4 border-t border-border/50">
                      <div>
                        <p className="font-semibold text-foreground">{review.name}</p>
                      </div>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Facebook className="h-3 w-3" />
                        {review.source}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* See More Reviews */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-center mt-10"
          >
            <Button 
              asChild 
              size="lg"
              className="btn-beveled px-8 py-6"
            >
              <a
                href="https://www.facebook.com/LittleHouseontheTable/reviews"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <Facebook className="h-5 w-5" />
                See All Reviews on Facebook
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </motion.div>
        </div>
      </section>

      <FloralSeparator />

      {/* Featured Review */}
      <section className="py-20 bg-secondary relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <PaperLayerBg>
              <div className="p-8 md:p-12 text-center">
                <Quote className="h-12 w-12 mx-auto text-primary/30 mb-6" />
                <blockquote className="font-serif text-xl md:text-2xl text-foreground mb-6 leading-relaxed">
                  My family loves Little House on the Table's jams and jellies. My absolute 
                  favorite is the peach jalapeño jam! The perfect balance of sweet with just 
                  a little kick. I love serving it over cream cheese with crackers, and it's 
                  always a hit. You can really taste the quality and care put into their products.
                </blockquote>
                <div className="flex items-center justify-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-6 w-6 text-floral-sunflower fill-floral-sunflower" />
                  ))}
                </div>
                <p className="font-semibold text-primary text-lg">— Chelsie Deaver</p>
                <p className="text-muted-foreground flex items-center justify-center gap-1 mt-1">
                  <Facebook className="h-4 w-4" />
                  Facebook Review
                </p>
              </div>
            </PaperLayerBg>
          </motion.div>
        </div>
      </section>

      {/* Why Customers Love Us */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4"
            >
              Why Customers Love Us
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
          >
            {[
              {
                title: 'Made with Love',
                description: 'Every product is lovingly crafted by hand on our family ranch using natural ingredients.'
              },
              {
                title: 'Quality You Can Taste',
                description: 'From our famous peach jalapeño jam to our goat milk soaps, quality shines through.'
              },
              {
                title: 'Family Values',
                description: 'We are a 7th generation ranching family committed to sustainable practices.'
              }
            ].map((item, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="raised-card h-full text-center p-6 border-0">
                  <CardContent className="p-0">
                    <Heart className="h-10 w-10 mx-auto mb-4 text-accent" />
                    <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Leave a Review CTA */}
      <section className="py-20 bg-gradient-floral relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              Tried Our Products?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              We would love to hear about your experience! Leave us a review on Facebook 
              and let others know about your favorite Little House on the Table products.
            </p>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-primary/30 hover:bg-primary/5 px-8 py-6"
            >
              <a
                href="https://www.facebook.com/LittleHouseontheTable/reviews"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                Leave a Review on Facebook
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Follow Us CTA */}
      <section className="py-20 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute top-0 left-0 w-64 h-64 -translate-x-1/2 -translate-y-1/2" viewBox="0 0 200 200">
            <circle cx="100" cy="100" r="80" stroke="white" strokeWidth="2" fill="none" />
          </svg>
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <Facebook className="h-12 w-12 mx-auto mb-4 text-primary-foreground/80" />
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              Join Our Community
            </h2>
            <p className="text-primary-foreground/80 mb-8">
              Follow us on Facebook for updates, behind-the-scenes ranch life, 
              new product announcements, and more!
            </p>
            <Button 
              asChild 
              size="lg"
              className="bg-accent hover:bg-accent/90 text-accent-foreground px-10 py-6 text-base font-semibold"
            >
              <a
                href="https://www.facebook.com/LittleHouseontheTable"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <Facebook className="h-5 w-5" />
                Follow Little House on the Table
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ReviewsPage;
